import React from 'react';

import {
  fetchCommissions,
  fetchCommissionStructure,
  fetchCpaReport,
  fetchPerformance,
  getAffiliateError,
  listAffiliates,
} from '../api';
import { buildOverview, buildRoster, type AffiliateCardVM, type OverviewMetricVM } from '../mapping';
import type {
  Affiliate,
  AffiliateCommissionsReport,
  AffiliateCpaReport,
  AffiliatePerformance,
  CommissionStructure,
} from '../types';

export interface AffiliatesRosterData {
  overview: OverviewMetricVM[];
  roster: AffiliateCardVM[];
  /** The raw report pieces the overview + roster view-models are built from (buildOverview /
   *  buildRoster); the affiliate list also backs the summary card. The Reports tab fetches its
   *  own date-scoped data via useAffiliateReports, so it does NOT read these. */
  raw: {
    affiliates: Affiliate[];
    performance?: AffiliatePerformance;
    cpa?: AffiliateCpaReport;
    commissions?: AffiliateCommissionsReport;
    /** The commission plan assigned to each affiliate (by id), read per-affiliate (best-effort). */
    structures?: Map<string, CommissionStructure | undefined>;
  };
  loading: boolean;
  error?: string;
  errorCode?: string;
  reload: () => void;
}

const EMPTY: AffiliatesRosterData['raw'] = { affiliates: [] };

/**
 * The roster + overview for the current brand. The affiliate list is the backbone
 * (a failure here is the page-level error, e.g. AFFILIATES_EMAIL_NOT_VERIFIED); the
 * three reports are best-effort enrichment (one performance call carries both the
 * per-affiliate rows and the range totals) so a flaky report only degrades a figure
 * to its placeholder glyph, never blanks the roster.
 */
export function useAffiliatesRoster(casinoInstanceId: string | undefined): AffiliatesRosterData {
  const [reloadTick, setReloadTick] = React.useState(0);
  const [state, setState] = React.useState<{
    raw: AffiliatesRosterData['raw'];
    /** The brand `raw` was read for, so a failed re-read keeps it only for that same brand. */
    brand?: string;
    loading: boolean;
    error?: string;
    errorCode?: string;
  }>({ raw: EMPTY, loading: true });

  React.useEffect(() => {
    if (!casinoInstanceId) {
      setState({ raw: EMPTY, loading: true });
      return;
    }
    let active = true;
    setState((prev) => ({ ...prev, loading: true, error: undefined, errorCode: undefined }));
    (async () => {
      const affiliates = await listAffiliates(casinoInstanceId);
      const [performance, cpa, commissions] = await Promise.all([
        fetchPerformance(casinoInstanceId, { group_by: 'affiliate' }).catch(() => undefined),
        fetchCpaReport(casinoInstanceId).catch(() => undefined),
        fetchCommissions(casinoInstanceId).catch(() => undefined),
      ]);
      // Assigned commission plans: one /commission-structure per affiliate, in parallel and
      // best-effort. The monthly commissions report omits newly-assigned / zero-activity plans, so
      // the structure is read directly to show + filter by the assigned plan (and reflect a
      // just-assigned plan on reload). A failed one degrades that card to "No plan", never the list.
      const structureEntries = await Promise.all(
        affiliates.map((affiliate) =>
          fetchCommissionStructure(casinoInstanceId, affiliate.id)
            .then((structure) => [affiliate.id, structure] as const)
            .catch(() => [affiliate.id, undefined] as const),
        ),
      );
      const structures = new Map<string, CommissionStructure | undefined>(structureEntries);
      return { affiliates, performance, cpa, commissions, structures };
    })()
      .then((raw) => {
        if (active) setState({ raw, brand: casinoInstanceId, loading: false });
      })
      .catch((err: unknown) => {
        if (!active) return;
        const { message, code } = getAffiliateError(err);
        // A re-read that fails keeps this brand's last good roster (an open affiliate sheet, which looks its record up
        // in it, stays open with the owner's edits); only a read with nothing held for the brand shows the error.
        setState((prev) =>
          prev.brand === casinoInstanceId && prev.raw.affiliates.length > 0
            ? { ...prev, loading: false }
            : { raw: EMPTY, loading: false, error: message, errorCode: code },
        );
      });
    return () => {
      active = false;
    };
  }, [casinoInstanceId, reloadTick]);

  const reload = React.useCallback(() => setReloadTick((tick) => tick + 1), []);

  const overview = React.useMemo(
    () => buildOverview(state.raw.performance, state.raw.commissions),
    [state.raw.performance, state.raw.commissions],
  );
  const roster = React.useMemo(
    () => buildRoster(state.raw.affiliates, state.raw.performance, state.raw.cpa, state.raw.commissions, state.raw.structures),
    [state.raw],
  );

  return { overview, roster, raw: state.raw, loading: state.loading, error: state.error, errorCode: state.errorCode, reload };
}
